import { useEffect, useRef, useState } from "react";
import LibTable, {
  type ColumnOrColumnGroup,
  type DataGridProps,
  type RenderSortStatusProps
} from "react-data-grid";
import { cx } from "shared/utils";
import { Show } from "shared/components/helpers/Show";
import { NoRowsFallback } from "./slices/NoRowsFallback";
import config from "config/config";
import { TextSkeleton } from "shared/components/skeletons";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowUp02Icon } from "@hugeicons-pro/core-stroke-rounded";

import "react-data-grid/lib/styles.css";

const DEFAULT_STYLE = { contain: "inline-size" };
const DEFAULT_ROW_HEIGHT = 64;
const DEFAULT_HEADER_ROW_HEIGHT = 44;
const RESIZE_DEBOUNCE_MS = 20;
/**
 * A data grid table component with responsive behavior and automatic scrolling.
 *
 * @template T - The type of data objects in each row
 *
 * @param {ColumnOrColumnGroup<T>[]} columns - Array of column definitions. At least one column must not have a fixed width.
 * @param {T[]} rows - Array of data objects to display in the table
 * @param {number} [rowHeight=64] - Height of each row in pixels
 * @param {number} [headerRowHeight=44] - Height of the header row in pixels
 * @param {string} [wrapperClassName] - Additional CSS classes for the wrapper div
 * @param {string} [className] - Additional CSS classes for the LibTable component
 * @param {boolean} [loading] - If true, displays skeleton loaders instead of actual data
 * @param {(row: T) => void} [onRowClick] - Callback fired when a row is clicked
 * @param {React.ReactNode} [footer] - Optional footer content displayed below the table
 * @param {number} [perPageRows=10] - Number of rows to display per page (used for skeleton loading state)
 * @param {number} [viewPortHeight=window.innerHeight] - The viewport height to use for max-height calculations
 * @param {number} [viewPortHeightOffset=0] - Offset to subtract from viewport height (useful for headers/footers)
 *
 *
 * @throws {Error} If all columns have fixed widths (at least one must be flexible)
 *
 * @remarks
 * - Window width changes trigger table re-renders for responsive behavior
 * - The table height is automatically calculated based on row count and row height
 * - When loading is true, skeleton loaders are shown for the number of rows specified in perPageRows
 */
export function DataGridTable<T>({
  columns,
  rows,
  rowHeight = DEFAULT_ROW_HEIGHT,
  headerRowHeight = DEFAULT_HEADER_ROW_HEIGHT,
  wrapperClassName,
  className,
  loading,
  onRowClick,
  footer,
  perPageRows = 10,
  viewPortHeight = window.innerHeight,
  viewPortHeightOffset = 0,
  noRowsFallback,
  sortColumns,
  onSortColumnsChange,
  ...restProps
}: Omit<DataGridProps<T>, "className" | "style"> & {
  wrapperClassName?: string;
  loading?: boolean;
  className?: string;
  onRowClick?: (row: T) => void;
  footer?: React.ReactNode;
  perPageRows?: number;

  noRowsFallback?: React.ReactNode;

  viewPortHeight?: number;
  viewPortHeightOffset?: number;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    if (footerRef.current)
      setFooterHeight(footerRef.current.getBoundingClientRect().height);
  }, [footer]);

  // At least one free width column is required for the DataGrid to render correctly.
  if (
    !columns.some(
      (col: ColumnOrColumnGroup<T>) =>
        (col as { width?: number | string } | undefined)?.width === undefined
    ) &&
    config.environment !== "production"
  )
    throw new Error(
      "At least one column must have a width defined or be frozen to render the DataGrid correctly."
    );

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Make sure last column doesn't have width
  columns = columns.map((col, index) => {
    if (index === columns.length - 1)
      return {
        ...col,
        width: undefined
      };

    return col;
  });

  if (loading)
    columns = columns.map((col) => ({
      ...col,
      renderCell: () => <TextSkeleton size="sm" />
    }));

  const wrapperHeight =
    (typeof rowHeight === "number"
      ? rowHeight || DEFAULT_ROW_HEIGHT
      : DEFAULT_ROW_HEIGHT) *
      (rows.length || perPageRows) +
    (headerRowHeight || DEFAULT_HEADER_ROW_HEIGHT) +
    // BORDERS width
    2 +
    footerHeight;

  return (
    <div
      ref={wrapperRef}
      className={cx(
        wrapperClassName,
        "flex flex-col rounded-lg border border-alpha-100"
      )}
      style={{
        ...DEFAULT_STYLE,
        maxHeight:
          wrapperHeight > viewPortHeight - viewPortHeightOffset
            ? viewPortHeight - viewPortHeightOffset
            : wrapperHeight,
        overflowY: "hidden"
      }}
    >
      <LibTable
        key={windowWidth}
        columns={columns}
        rows={loading ? Array(perPageRows) : rows}
        rowHeight={rowHeight}
        headerRowHeight={headerRowHeight}
        renderers={{
          noRowsFallback: noRowsFallback || <NoRowsFallback />,
          renderSortStatus
        }}
        className={cx("boom-react-data-grid overscroll-none h-fit", className)}
        rowClass={onRowClick ? () => "cursor-pointer" : undefined}
        onCellClick={(args) => onRowClick?.(args.row)}
        sortColumns={sortColumns}
        onSortColumnsChange={onSortColumnsChange}
        {...restProps}
      />

      <Show if={footer}>
        <div
          ref={footerRef}
          className="border-t border-alpha-40 px-12 py-4 text-content-1000 bg-white"
        >
          {footer}
        </div>
      </Show>
    </div>
  );
}
const renderSortStatus = ({ sortDirection }: RenderSortStatusProps) => {
  if (!sortDirection) return null;
  return (
    <HugeiconsIcon
      icon={ArrowUp02Icon}
      size="14"
      strokeWidth={2}
      className={cx(
        "transition-rotate shrink-0",
        sortDirection === "ASC" ? "rotate-0" : "rotate-180"
      )}
    />
  );
};
